import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { GlassCard, Chip, Skeleton, Modal, Table, Bar, Pagination, AV_FALLBACK, PageHead } from "@/v2/admin/ui";
import { useDept, downloadDeptPdf } from "./DeptShell";
import {
  Users, Search, Flame, Award, Eye, Download, Send, Save, Loader2, Activity, Coins, CheckCircle2, LogIn,
} from "lucide-react";

const T_ICON = { quiz: Activity, challenge: CheckCircle2, points: Coins, login: LogIn, badge: Award };

function EmpDrawer({ uid, onClose }) {
  const { q, department } = useDept();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["dept-emp", uid, q],
    queryFn: async () => (await api.get(`/dept/employees/${uid}?${q}`)).data,
  });
  const [f, setF] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  if (isLoading || !data) return <Modal title="ملف الموظف" onClose={onClose} wide testId="dept-emp-drawer"><Skeleton className="h-64 w-full" /></Modal>;
  const u = data.user;
  const form = f || { job_title: u.job_title || "", section: u.section || "" };

  const save = async () => {
    try {
      await api.patch(`/dept/employees/${uid}?${q}`, form);
      toast.success("تم تحديث بيانات الموظف");
      qc.invalidateQueries({ queryKey: ["dept-emp", uid] });
      qc.invalidateQueries({ queryKey: ["dept-emps"] });
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر التعديل"); }
  };
  const motivate = async () => {
    if (!msg.trim()) return toast.error("اكتب نص الرسالة");
    try {
      await api.post(`/dept/motivate?${q}`, { user_id: uid, title: "رسالة تحفيزية", body: msg });
      toast.success("تم إرسال الرسالة التحفيزية");
      setMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الإرسال"); }
  };
  const pdf = async () => {
    setBusy(true);
    try {
      await downloadDeptPdf({ kind: "employee", user_id: uid, department }, `masar-employee-${u.employee_id || uid}.pdf`);
      toast.success("تم إنشاء تقرير الموظف");
    } catch { toast.error("تعذّر إنشاء التقرير"); } finally { setBusy(false); }
  };

  return (
    <Modal title="الملف الكامل للموظف" onClose={onClose} wide testId="dept-emp-drawer">
      <div className="flex items-center gap-4 mb-5">
        <img src={u.avatar} alt={u.name} onError={AV_FALLBACK} className="w-16 h-16 rounded-full object-cover border-2 border-[#bb9661]/50" />
        <div className="flex-1">
          <div className="font-extrabold text-lg">{u.name}</div>
          <div className="text-sm text-[#bcc8cf]">{u.job_title} — {u.section || "—"} — {u.employee_id}</div>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            <Chip tone="emerald">{u.level}</Chip>
            <Chip tone="gold">ترتيب الإدارة #{data.dept_rank || "—"}</Chip>
            <Chip tone="muted">الترتيب العام #{data.rank || "—"}</Chip>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-5">
        {[["النقاط", u.points || 0], ["مكتمل", (u.completed_challenge_ids || []).length], ["سلسلة التعلّم", u.streak || 0],
          ["الشارات", (u.badges || []).length], ["المحاولات", data.attempts.length], ["نسبة الإنجاز", `${data.completion_pct}%`]].map(([l, v]) => (
          <div key={l} className="v2-inset p-3 text-center">
            <div className="text-lg font-extrabold v2-gold-text">{v}</div>
            <div className="text-[10px] text-[#bcc8cf]">{l}</div>
          </div>
        ))}
      </div>
      <div className="mb-5"><Bar value={data.completion_pct} /></div>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <div>
          <h4 className="font-bold text-sm mb-2">التحديات</h4>
          <div className="v2-inset p-3 max-h-44 overflow-y-auto space-y-1.5" data-testid="dept-emp-challenges">
            {data.challenges.map((c) => (
              <div key={c.id} className="flex justify-between items-center text-xs gap-2">
                <span className="text-[#bcc8cf] truncate">اليوم {c.day} — {c.title}</span>
                <Chip tone={c.state === "completed" ? "emerald" : c.state === "in_progress" ? "gold" : "muted"}>
                  {c.state === "completed" ? `مكتمل ${c.best_score || 0}%` : c.state === "in_progress" ? "جارٍ" : "لم يبدأ"}
                </Chip>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4 className="font-bold text-sm mb-2">سجل النشاط</h4>
          <div className="v2-inset p-3 max-h-44 overflow-y-auto space-y-1.5" data-testid="dept-emp-timeline">
            {data.timeline.length === 0 ? <p className="text-xs text-[#bcc8cf]">لا يوجد نشاط.</p> : data.timeline.map((t, i) => {
              const I = T_ICON[t.kind] || Activity;
              return (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <I size={13} className="text-[#91cab8] mt-0.5 shrink-0" />
                  <span className="text-[#bcc8cf]">{t.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {(u.badges_named || []).length > 0 && (
        <div className="mb-5 flex gap-2 flex-wrap">
          {u.badges_named.map((b) => <Chip key={b} tone="gold"><Award size={12} /> {b}</Chip>)}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-white/10">
        <div className="space-y-2">
          <h4 className="font-bold text-sm">تعديل بيانات محدودة</h4>
          <div><label className="v2-label">المسمى الوظيفي</label>
            <input value={form.job_title} onChange={(e) => setF({ ...form, job_title: e.target.value })}
              data-testid="dept-emp-job" className="v2-input" /></div>
          <div><label className="v2-label">القسم داخل الإدارة</label>
            <input value={form.section} onChange={(e) => setF({ ...form, section: e.target.value })}
              data-testid="dept-emp-section" className="v2-input" /></div>
          <button onClick={save} data-testid="dept-emp-save" className="v2-btn v2-btn-sm w-full"><Save size={14} /> حفظ التعديلات</button>
        </div>
        <div className="space-y-2">
          <h4 className="font-bold text-sm">رسالة تحفيزية وتقرير</h4>
          <textarea rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="اكتب رسالة تحفيزية للموظف..."
            data-testid="dept-emp-msg" className="v2-input resize-none" />
          <div className="flex gap-2">
            <button onClick={motivate} data-testid="dept-emp-motivate" className="v2-btn v2-btn-sm flex-1"><Send size={14} /> إرسال</button>
            <button onClick={pdf} disabled={busy} data-testid="dept-emp-pdf" className="v2-btn-ghost v2-btn-sm flex-1">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} تقرير PDF
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function DeptEmployees() {
  const { q, meta } = useDept();
  const [search, setSearch] = useState("");
  const [section, setSection] = useState("");
  const [level, setLevel] = useState("");
  const [minDone, setMinDone] = useState(0);
  const [sort, setSort] = useState("points");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["dept-emps", q, search, section, level, minDone, sort, page],
    queryFn: async () => (await api.get(
      `/dept/employees?${q}&search=${encodeURIComponent(search)}&section=${encodeURIComponent(section)}&level=${encodeURIComponent(level)}&min_completed=${minDone}&sort=${sort}&page=${page}&limit=15`)).data,
  });
  const rows = data?.rows || [];

  return (
    <div className="space-y-5" data-testid="dept-employees">
      <PageHead icon={Users} title="موظفو إدارتي" sub="متابعة تفصيلية لكل موظف داخل إدارتك فقط." />

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute top-1/2 -translate-y-1/2 right-4 text-[#bcc8cf]" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="dept-emp-search"
            placeholder="ابحث بالاسم أو الرقم الوظيفي..." className="v2-input" style={{ paddingInlineStart: "2.75rem" }} />
        </div>
        <select value={section} onChange={(e) => { setSection(e.target.value); setPage(1); }} data-testid="dept-emp-section-filter" className="v2-input !w-auto text-sm font-bold">
          <option value="">كل الأقسام</option>
          {(meta?.sections || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={level} onChange={(e) => { setLevel(e.target.value); setPage(1); }} data-testid="dept-emp-level" className="v2-input !w-auto text-sm font-bold">
          <option value="">كل المستويات</option>
          {(data?.levels || []).map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={minDone} onChange={(e) => { setMinDone(e.target.value); setPage(1); }} data-testid="dept-emp-min" className="v2-input !w-auto text-sm font-bold">
          <option value={0}>كل الإنجازات</option>
          <option value={1}>أكمل تحديًا واحدًا على الأقل</option>
          <option value={5}>5 تحديات أو أكثر</option>
          <option value={10}>10 تحديات أو أكثر</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="dept-emp-sort" className="v2-input !w-auto text-sm font-bold">
          <option value="points">الأعلى نقاطًا</option>
          <option value="completion">الأعلى إنجازًا</option>
          <option value="streak">الأطول سلسلة</option>
          <option value="activity">الأحدث نشاطًا</option>
          <option value="name">الاسم</option>
        </select>
      </div>

      {isLoading ? <Skeleton className="h-72 w-full" /> : rows.length === 0 ? (
        <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا توجد نتائج مطابقة.</GlassCard>
      ) : (
        <Table head={["الموظف", "القسم", "المستوى", "النقاط", "الإنجاز", "متتالية", "الشارات", ""]} min={880} testId="dept-emp-table">
          {rows.map((e) => (
            <tr key={e.id} data-testid="dept-emp-row">
              <td>
                <div className="flex items-center gap-3">
                  <img src={e.avatar} alt={e.name} onError={AV_FALLBACK} className="w-9 h-9 rounded-full object-cover" />
                  <div><div className="font-semibold">{e.name}</div><div className="text-[10px] text-[#bcc8cf]">{e.employee_id} · {e.job_title}</div></div>
                </div>
              </td>
              <td className="text-[#bcc8cf]">{e.section || "—"}</td>
              <td><Chip tone="emerald">{e.level}</Chip></td>
              <td className="font-extrabold v2-gold-text">{e.points}</td>
              <td style={{ minWidth: 120 }}>
                <div className="text-[10px] text-[#bcc8cf] mb-1">{e.completed} تحديًا · {e.completion_pct}%</div>
                <Bar value={e.completion_pct} />
              </td>
              <td><span className="inline-flex items-center gap-1"><Flame size={13} className="text-[#F7A08A]" /> {e.streak || 0}</span></td>
              <td><span className="inline-flex items-center gap-1"><Award size={13} className="text-[#e3c799]" /> {(e.badges || []).length}</span></td>
              <td><button onClick={() => setSelected(e.id)} data-testid={`dept-emp-view-${e.employee_id}`} className="v2-btn-ghost v2-btn-sm"><Eye size={14} /> عرض</button></td>
            </tr>
          ))}
        </Table>
      )}

      <Pagination page={page} pages={data?.pages || 1} onChange={setPage} />
      {selected && <EmpDrawer uid={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
